'use client'

import { createContext, useContext, type ReactNode } from 'react'

import qs from 'qs'

type HttpClient = {
  get: (url: string, config?: unknown) => Promise<any>
  post: (url: string, data?: unknown, config?: unknown) => Promise<any>
  put: (url: string, data?: unknown, config?: unknown) => Promise<any>
  delete: (url: string, config?: unknown) => Promise<any>
  patch?: (url: string, data?: unknown, config?: unknown) => Promise<any>
}

type ResourcePath = string | ((id: unknown) => string)

export type DataProvider = {
  getList: (resource: string, params?: any) => Promise<{ records: unknown[]; pagination?: unknown }>
  create: (resource: string, data: unknown) => Promise<any>
  update: (resource: string, id: unknown, data: unknown) => Promise<any>
  customAction: (resource: string, id: unknown, action: string, method: string, data: unknown) => Promise<any>
  delete: (resource: string, id: unknown) => Promise<any>
}

export function useDataProvider(
  apiUrl: string,
  httpClient: HttpClient,
  sendFormAsFormData = false
): DataProvider {
  const getPath = (
    resource: ResourcePath,
    id?: unknown,
    action?: string,
    query?: Record<string, unknown>
  ) => {
    const resourcePath =
      typeof resource === 'function'
        ? resource(id)
        : `${resource}${id != null ? `/${id}` : ''}${action ? `/${action}` : ''}`
    const queryString = query ? qs.stringify(query, { arrayFormat: 'brackets' }) : ''
    const fullPath = `${apiUrl}${resourcePath}`
    const sep = fullPath.includes('?') ? '&' : '?'
    return queryString ? `${fullPath}${sep}${queryString}` : fullPath
  }

  const wrapData = (data: unknown) =>
    sendFormAsFormData
      ? (() => { const fd = new FormData(); fd.append('record', JSON.stringify(data)); return fd })()
      : { record: data }

  return {
    getList: (resource, { pagination, sort, filter, params } = {}) => {
      const query = params ?? { pagination, sort, q: filter }
      return httpClient
        .get(getPath(resource, null, undefined, query))
        .then(({ data: { records, pagination: p } }: any) => ({ records, pagination: p }))
    },
    create: (resource, data) => httpClient.post(getPath(resource), wrapData(data)),
    update: (resource, id, data) => httpClient.put(getPath(resource, id), wrapData(data)),
    customAction: (resource, id, action, method, data) => {
      const url = getPath(resource, id, action)
      const m = (method ?? 'post').toLowerCase()
      const body = wrapData(data)
      if (m === 'get' || m === 'delete') {
        return (httpClient as any)[m](url)
      }
      return (httpClient as any)[m](url, body)
    },
    delete: (resource, id) => httpClient.delete(getPath(resource, id))
  }
}

type DataProviderContextValue = { dataProvider: DataProvider }
const DataProviderContext = createContext<DataProviderContextValue | null>(null)

export function useDataProviderContext(): DataProviderContextValue {
  const ctx = useContext(DataProviderContext)
  if (!ctx) throw new Error('useDataProviderContext must be used within DataProviderProvider')
  return ctx
}

export function DataProviderProvider({
  dataProvider,
  children
}: {
  dataProvider: DataProvider
  children: ReactNode
}) {
  return (
    <DataProviderContext.Provider value={{ dataProvider }}>
      {children}
    </DataProviderContext.Provider>
  )
}
